/* istanbul ignore file */
/* eslint-disable */
import React from "react";
import "./scenario-table.scss";
import classNames from "classnames";
import {
  ToelichtingOptions,
  MotiveringCompleetOutput,
  InkomensanalyseResultaatAanvrager,
  InkomensanalyseResultaat,
  ToelichtingRegel,
} from "../../../.generated/rapportage/rapportagetypes";
import { TableNettoBesteedbaarInkomen } from "./nbi";
import { monthDivision } from "./infra/scenario-utils";
import {
  CitationProvider,
  excludedFootnotes,
  CitationNote,
  CitationRef,
} from "./citation-notes";
import { ControleMaandlastOptions } from "./infra/utils";
import {
  ControleGewenstInkomenOptions,
  DashboardScenarioArbeidsongeschiktheid,
} from "../../../.generated/rapportage/rapportagetypes";
import Table from "../components/Table";
import { Column } from "../components/BaseTable";
import { BenodigdKapitaalTable } from "./tables/benodigd-kapitaal-table";
import { ControleMaandlastTable } from "./tables/controle-maandlast-table";
import { MaximaleHypotheekTable } from "./tables/maximale-hypotheek";
import { ControleWerkelijkeLastenTable } from "./tables/controle-werkelijke-lasten-table";
import { BrutoInkomensWensenTable } from "./tables/bruto-inkomens-wensen";
import { BenodigdInkomenTable } from "./tables/benodigd-inkomen-table";
import Heading from "../components/Heading";

export type ArbeidsongeschiktheidDrawerProps = {
  motiveringOutput: MotiveringCompleetOutput | null;
  dashboardSettings: DashboardScenarioArbeidsongeschiktheid;
  onKlantIdChange?: (klantId: string) => void;
  loading?: boolean;
  selectedAanvrager?: "Aanvrager1" | "Aanvrager2";
};

function optellen(values: Array<number | null | undefined>): number {
  return values.reduce((sum: number, value) => sum + (value ?? 0), 0);
}

const getMonthlyAmount = (
  annualAmount: number | null | undefined,
): number | null => {
  if (annualAmount == null) return null;
  return monthDivision(annualAmount);
};

export const InkomensanalyseTabelArbeidsongeschiktheid: React.FC<ArbeidsongeschiktheidDrawerProps> = ({
  motiveringOutput,
  dashboardSettings,
  loading,
  selectedAanvrager,
}) => {
  const motiveringInkomensanalyse = motiveringOutput?.motiveringInkomensanalyse;
  const emptyResultaat: InkomensanalyseResultaat = {
    benodigdInkomenBedrag: null,
    tekortBenodigInkomenBedrag: null,
    tekortNettoInkomenBedrag: null,
    maximaleHypotheekBedrag: null,
    hypotheeksaldoBedrag: null,
  } as InkomensanalyseResultaat;
  const baseResultaten = motiveringInkomensanalyse?.resultaten || [];

  const inkomensanalyseResultaten: InkomensanalyseResultaat[] = [
    ...baseResultaten,
    ...Array(Math.max(0, 3 - baseResultaten.length)).fill(emptyResultaat),
  ];
  const totaalHuidigBrutoJaarinkomenBedrag =
    motiveringInkomensanalyse?.totaalHuidigBrutoJaarinkomenBedrag || null;
  const scenarioGegevens = motiveringOutput?.scenarioGegevens;
  const normGegevens = motiveringOutput?.normGegevens;

  const getInkomstenPeriodes = (partner: boolean) => {
    const resultatenList = inkomensanalyseResultaten;
    const selectAanvrager = (
      periodResult: InkomensanalyseResultaat | undefined | null,
    ) => (partner ? periodResult?.aanvrager2 : periodResult?.aanvrager1);
    const selectPartner = (
      periodResult: InkomensanalyseResultaat | undefined | null,
    ) => (partner ? periodResult?.aanvrager1 : periodResult?.aanvrager2);

    return {
      aanvragerInkomstenPeriodeEerst: selectAanvrager(resultatenList?.[0]) as
        | InkomensanalyseResultaatAanvrager
        | undefined,
      aanvragerInkomstenPeriodeTweede: selectAanvrager(resultatenList?.[1]) as
        | InkomensanalyseResultaatAanvrager
        | undefined,
      aanvragerInkomstenPeriodeDerde: selectAanvrager(resultatenList?.[2]) as
        | InkomensanalyseResultaatAanvrager
        | undefined,
      partnerInkomstenPeriodeEerste: selectPartner(resultatenList?.[0]) as
        | InkomensanalyseResultaatAanvrager
        | undefined,
      partnerInkomstenPeriodeTweede: selectPartner(resultatenList?.[1]) as
        | InkomensanalyseResultaatAanvrager
        | undefined,
      partnerInkomstenPeriodeDerde: selectPartner(resultatenList?.[2]) as
        | InkomensanalyseResultaatAanvrager
        | undefined,
    };
  };

  const {
    aanvragerInkomstenPeriodeEerst,
    aanvragerInkomstenPeriodeTweede,
    aanvragerInkomstenPeriodeDerde,
    partnerInkomstenPeriodeEerste,
    partnerInkomstenPeriodeTweede,
    partnerInkomstenPeriodeDerde,
  } = getInkomstenPeriodes(selectedAanvrager === "Aanvrager2");

  const nbiWeergeven =
    (dashboardSettings.uitgangspunten?.controleGewenstInkomen
      ?.controleGewenstInkomen === ControleGewenstInkomenOptions.Nbi &&
      !!dashboardSettings.uitgangspunten?.controleHypotheeknorm &&
      !!dashboardSettings.uitgangspunten?.controleGewenstInkomen
        ?.gewijzigdeJarenTonen) ||
    false;
  const maximaleHypotheekWeergeven =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleerMaximaleHypotheek || false;
  const benodigdKapitaalWeergeven =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.benodigdKapitaalTonen || false;
  const controleWerkelijkeLastenWeergeven =
    (dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast ||
      false) &&
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast === ControleMaandlastOptions.WerkelijkeLast;
  const brutoInkomensWensenWeergeven =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.controleGewenstInkomen === "BrutoInkomenswens";
  const brutoInkomensWensPercentage =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.brutoInkomenswensPercentage || 0;
  const controleMaandlast =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast || false;
  const controleMaandlastKeuze =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast || null;

  const conclusieEerste =
    inkomensanalyseResultaten?.[0]?.conclusie?.toString() || "";
  const conclusieTweede =
    inkomensanalyseResultaten?.[1]?.conclusie?.toString() || "";
  const conclusieDerde =
    inkomensanalyseResultaten?.[2]?.conclusie?.toString() || "";
  const nbiVoetnoot =
    motiveringInkomensanalyse?.nbiOverzicht?.voetnoot?.voetnoot || "";
  const nbi = motiveringInkomensanalyse?.nbiOverzichten?.["1"]?.jaren || [];
  const voetnoten = motiveringOutput?.toelichtingRegels || [];
  const benodigdKapitaal = motiveringOutput?.benodigdKapitaal?.bedrag ?? null;
  const benodigdKapitaalOmschrijving =
    motiveringOutput?.benodigdKapitaal?.omschrijving || "";

  // Herbruikbare functie voor het berekenen van totaal inkomen per periode
  const berekenTotaalInkomenPeriode = (
    aanvragerInkomsten: InkomensanalyseResultaatAanvrager | undefined,
    partnerInkomsten: InkomensanalyseResultaatAanvrager | undefined,
  ) => {
    return optellen([
      aanvragerInkomsten?.wgaUitkeringBedrag,
      aanvragerInkomsten?.wgaGatVerzekeringBedrag,
      aanvragerInkomsten?.wgaGatVerzekeringBruteringBedrag,
      aanvragerInkomsten?.wiaExcedentVerzekeringBedrag,
      aanvragerInkomsten?.wiaExcedentVerzekeringBruteringBedrag,
      aanvragerInkomsten?.restInkomenBedrag,
      partnerInkomsten?.restInkomenBedrag,
      aanvragerInkomsten?.ivaUitkeringBedrag,
      aanvragerInkomsten?.invaliditeitspensioenBedrag,
      aanvragerInkomsten?.werkgeverspensioenBedrag,
      berekenInkomstenOverigAanvragerPeriode(aanvragerInkomsten),
      berekenInkomstenOverigPartnerPeriode(partnerInkomsten),
      (aanvragerInkomsten?.beleggingenBedrag || 0) +
        (aanvragerInkomsten?.beleggingenBruteringBedrag || 0),
      (aanvragerInkomsten?.verhuurBedrag || 0) +
        (aanvragerInkomsten?.verhuurBruteringBedrag || 0),
      (partnerInkomsten?.beleggingenBedrag || 0) +
        (partnerInkomsten?.beleggingenBruteringBedrag || 0),
      (partnerInkomsten?.verhuurBedrag || 0) +
        (partnerInkomsten?.verhuurBruteringBedrag || 0),
      (aanvragerInkomsten?.woonlastenverzekeringBedrag || 0) +
        (aanvragerInkomsten?.woonlastenverzekeringBruteringBedrag || 0),
    ]);
  };

  // Herbruikbare functie voor het berekenen van inkomensverlies per periode
  const berekenInkomensverliesPeriode = (
    aanvragerInkomsten: InkomensanalyseResultaatAanvrager | undefined,
    partnerInkomsten: InkomensanalyseResultaatAanvrager | undefined,
  ) => {
    return Math.max(
      0,
      (totaalHuidigBrutoJaarinkomenBedrag || 0) / 12 -
        (berekenTotaalInkomenPeriode(aanvragerInkomsten, partnerInkomsten) ||
          0) /
          12,
    );
  };

  // Herbruikbare functie voor het berekenen van inkomsten overig aanvrager per periode
  const berekenInkomstenOverigAanvragerPeriode = (
    aanvragerInkomsten: InkomensanalyseResultaatAanvrager | undefined,
  ) => {
    return (
      (aanvragerInkomsten?.inkomstenOverigBedrag || 0) +
      (aanvragerInkomsten?.inkomstenOverigBruteringBedrag || 0)
    );
  };

  // Herbruikbare functie voor het berekenen van inkomsten overig partner per periode
  const berekenInkomstenOverigPartnerPeriode = (
    partnerInkomsten: InkomensanalyseResultaatAanvrager | undefined,
  ) => {
    return (
      (partnerInkomsten?.inkomstenOverigBedrag || 0) +
      (partnerInkomsten?.inkomstenOverigBruteringBedrag || 0)
    );
  };

  const inkomensData = {
    aoUitkeringAanvragerEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.arbeidsongeschiktheidsverzekeringBedrag ||
        0) +
      (partnerInkomstenPeriodeEerste?.arbeidsongeschiktheidsverzekeringBedrag ||
        0),
    aoUitkeringAanvragerTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.arbeidsongeschiktheidsverzekeringBedrag ||
        0) +
      (partnerInkomstenPeriodeTweede?.arbeidsongeschiktheidsverzekeringBedrag ||
        0),
    aoUitkeringAanvragerDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.arbeidsongeschiktheidsverzekeringBedrag ||
        0) +
      (partnerInkomstenPeriodeDerde?.arbeidsongeschiktheidsverzekeringBedrag ||
        0),
    naamPersoon:
      selectedAanvrager === "Aanvrager2"
        ? motiveringOutput?.klantNaamPartner || "Partner"
        : motiveringOutput?.klantNaamAanvrager || "Aanvrager",
    naamPartner:
      selectedAanvrager === "Aanvrager1"
        ? motiveringOutput?.klantNaamPartner || "Partner"
        : motiveringOutput?.klantNaamAanvrager || "Aanvrager",
    inkomenAanvragerEerstePeriode:
      aanvragerInkomstenPeriodeEerst?.restInkomenBedrag ?? null,
    inkomenAanvragerTweedePeriode:
      aanvragerInkomstenPeriodeTweede?.restInkomenBedrag ?? null,
    inkomenAanvragerDerdePeriode:
      aanvragerInkomstenPeriodeDerde?.restInkomenBedrag ?? null,
    inkomenPartnerEerstePeriode:
      partnerInkomstenPeriodeEerste?.restInkomenBedrag ?? null,
    inkomenPartnerTweedePeriode:
      partnerInkomstenPeriodeTweede?.restInkomenBedrag ?? null,
    inkomenPartnerDerdePeriode:
      partnerInkomstenPeriodeDerde?.restInkomenBedrag ?? null,
    beleggingInkomenEerste:
      (aanvragerInkomstenPeriodeEerst?.beleggingenBedrag || 0) +
      (aanvragerInkomstenPeriodeEerst?.beleggingenBruteringBedrag || 0),
    beleggingInkomenTweede:
      (aanvragerInkomstenPeriodeTweede?.beleggingenBedrag || 0) +
      (aanvragerInkomstenPeriodeTweede?.beleggingenBruteringBedrag || 0),
    beleggingInkomenDerde:
      (aanvragerInkomstenPeriodeDerde?.beleggingenBedrag || 0) +
      (aanvragerInkomstenPeriodeDerde?.beleggingenBruteringBedrag || 0),
    beleggingInkomenPartnerEerste:
      (partnerInkomstenPeriodeEerste?.beleggingenBedrag || 0) +
      (partnerInkomstenPeriodeEerste?.beleggingenBruteringBedrag || 0),
    beleggingInkomenPartnerTweede:
      (partnerInkomstenPeriodeTweede?.beleggingenBedrag || 0) +
      (partnerInkomstenPeriodeTweede?.beleggingenBruteringBedrag || 0),
    beleggingInkomenPartnerDerde:
      (partnerInkomstenPeriodeDerde?.beleggingenBedrag || 0) +
      (partnerInkomstenPeriodeDerde?.beleggingenBruteringBedrag || 0),
    verhuurInkomenEerste:
      (aanvragerInkomstenPeriodeEerst?.verhuurBedrag || 0) +
      (aanvragerInkomstenPeriodeEerst?.verhuurBruteringBedrag || 0),
    verhuurInkomenTweede:
      (aanvragerInkomstenPeriodeTweede?.verhuurBedrag || 0) +
      (aanvragerInkomstenPeriodeTweede?.verhuurBruteringBedrag || 0),
    verhuurInkomenDerde:
      (aanvragerInkomstenPeriodeDerde?.verhuurBedrag || 0) +
      (aanvragerInkomstenPeriodeDerde?.verhuurBruteringBedrag || 0),
    verhuurInkomenPartnerEerste:
      (partnerInkomstenPeriodeEerste?.verhuurBedrag || 0) +
      (partnerInkomstenPeriodeEerste?.verhuurBruteringBedrag || 0),
    verhuurInkomenPartnerTweede:
      (partnerInkomstenPeriodeTweede?.verhuurBedrag || 0) +
      (partnerInkomstenPeriodeTweede?.verhuurBruteringBedrag || 0),
    verhuurInkomenPartnerDerde:
      (partnerInkomstenPeriodeDerde?.verhuurBedrag || 0) +
      (partnerInkomstenPeriodeDerde?.verhuurBruteringBedrag || 0),
    werkgeverspensioenEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.werkgeverspensioenBedrag || 0) +
      (partnerInkomstenPeriodeEerste?.werkgeverspensioenBedrag || 0),
    werkgeverspensioenTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.werkgeverspensioenBedrag || 0) +
      (partnerInkomstenPeriodeTweede?.werkgeverspensioenBedrag || 0),
    werkgeverspensioenDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.werkgeverspensioenBedrag || 0) +
      (partnerInkomstenPeriodeDerde?.werkgeverspensioenBedrag || 0),
    invaliditeitspensioenEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.invaliditeitspensioenBedrag || 0) +
      (partnerInkomstenPeriodeEerste?.invaliditeitspensioenBedrag || 0),
    invaliditeitspensioenTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.invaliditeitspensioenBedrag || 0) +
      (partnerInkomstenPeriodeTweede?.invaliditeitspensioenBedrag || 0),
    invaliditeitspensioenDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.invaliditeitspensioenBedrag || 0) +
      (partnerInkomstenPeriodeDerde?.invaliditeitspensioenBedrag || 0),
    wgaUitkeringAanvragerEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.wgaUitkeringBedrag || 0) +
      (partnerInkomstenPeriodeEerste?.wgaUitkeringBedrag || 0),
    wgaUitkeringAanvragerTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.wgaUitkeringBedrag || 0) +
      (partnerInkomstenPeriodeTweede?.wgaUitkeringBedrag || 0),
    wgaUitkeringAanvragerDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.wgaUitkeringBedrag || 0) +
      (partnerInkomstenPeriodeDerde?.wgaUitkeringBedrag || 0),
    wgaGatVerzekeringEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.wgaGatVerzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeEerst?.wgaGatVerzekeringBruteringBedrag || 0),
    wgaGatVerzekeringTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.wgaGatVerzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeTweede?.wgaGatVerzekeringBruteringBedrag || 0),
    wgaGatVerzekeringDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.wgaGatVerzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeDerde?.wgaGatVerzekeringBruteringBedrag || 0),
    wiaExcedentVerzekeringEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.wiaExcedentVerzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeEerst?.wiaExcedentVerzekeringBruteringBedrag ||
        0),
    wiaExcedentVerzekeringTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.wiaExcedentVerzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeTweede?.wiaExcedentVerzekeringBruteringBedrag ||
        0),
    wiaExcedentVerzekeringDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.wiaExcedentVerzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeDerde?.wiaExcedentVerzekeringBruteringBedrag ||
        0),
    inkomstenOverigEerstePeriode: berekenInkomstenOverigAanvragerPeriode(
      aanvragerInkomstenPeriodeEerst,
    ),
    inkomstenOverigPartnerEerstePeriode: berekenInkomstenOverigPartnerPeriode(
      partnerInkomstenPeriodeEerste,
    ),
    inkomstenOverigTweedePeriode: berekenInkomstenOverigAanvragerPeriode(
      aanvragerInkomstenPeriodeTweede,
    ),
    inkomstenOverigPartnerTweedePeriode: berekenInkomstenOverigPartnerPeriode(
      partnerInkomstenPeriodeTweede,
    ),
    inkomstenOverigDerdePeriode: berekenInkomstenOverigAanvragerPeriode(
      aanvragerInkomstenPeriodeDerde,
    ),
    inkomstenOverigPartnerDerdePeriode: berekenInkomstenOverigPartnerPeriode(
      partnerInkomstenPeriodeDerde,
    ),
    totaalInkomenTweedePeriode: berekenTotaalInkomenPeriode(
      aanvragerInkomstenPeriodeTweede,
      partnerInkomstenPeriodeTweede,
    ),
    totaalInkomenEerstePeriode: berekenTotaalInkomenPeriode(
      aanvragerInkomstenPeriodeEerst,
      partnerInkomstenPeriodeEerste,
    ),
    totaalInkomenDerdePeriode: berekenTotaalInkomenPeriode(
      aanvragerInkomstenPeriodeDerde,
      partnerInkomstenPeriodeDerde,
    ),
    inkomensverliesEerste: berekenInkomensverliesPeriode(
      aanvragerInkomstenPeriodeEerst,
      partnerInkomstenPeriodeEerste,
    ),
    inkomensverliesTweede: berekenInkomensverliesPeriode(
      aanvragerInkomstenPeriodeTweede,
      partnerInkomstenPeriodeTweede,
    ),
    inkomensverliesDerde: berekenInkomensverliesPeriode(
      aanvragerInkomstenPeriodeDerde,
      partnerInkomstenPeriodeDerde,
    ),
    percentageAoEerste: scenarioGegevens?.percentageArbeidsongeschiktheid || 0,
    percentageVerdienEerste: scenarioGegevens?.verdiencapaciteitPercentage || 0,
    percentageAoTweede:
      scenarioGegevens?.percentageArbeidsongeschiktheidExtra1 || 0,
    percentageVerdienTweede:
      scenarioGegevens?.verdiencapaciteitPercentageExtra1 || 0,
    percentageAoDerde:
      scenarioGegevens?.percentageArbeidsongeschiktheidExtra2 || 0,
    percentageVerdienDerde:
      scenarioGegevens?.verdiencapaciteitPercentageExtra2 || 0,
    ivaUitkeringAanvragerEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.ivaUitkeringBedrag || 0) +
      (partnerInkomstenPeriodeEerste?.ivaUitkeringBedrag || 0),
    ivaUitkeringAanvragerTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.ivaUitkeringBedrag || 0) +
      (partnerInkomstenPeriodeTweede?.ivaUitkeringBedrag || 0),
    ivaUitkeringAanvragerDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.ivaUitkeringBedrag || 0) +
      (partnerInkomstenPeriodeDerde?.ivaUitkeringBedrag || 0),
    woonlastenEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.woonlastenverzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeEerst?.woonlastenverzekeringBruteringBedrag ||
        0),
    woonlastenTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.woonlastenverzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeTweede?.woonlastenverzekeringBruteringBedrag ||
        0),
    woonlastenDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.woonlastenverzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeDerde?.woonlastenverzekeringBruteringBedrag ||
        0),
    rvcEerste:
      ((100 - (scenarioGegevens?.percentageArbeidsongeschiktheid || 0)) *
        (scenarioGegevens?.verdiencapaciteitPercentage || 0)) /
      100,
    rvcTweede:
      ((100 - (scenarioGegevens?.percentageArbeidsongeschiktheidExtra1 || 0)) *
        (scenarioGegevens?.verdiencapaciteitPercentageExtra1 || 0)) /
      100,
    rvcDerde:
      ((100 - (scenarioGegevens?.percentageArbeidsongeschiktheidExtra2 || 0)) *
        (scenarioGegevens?.verdiencapaciteitPercentageExtra2 || 0)) /
      100,
    brutoInkomenPartnerEerstePeriode:
      partnerInkomstenPeriodeEerste?.restInkomenBedrag,
    brutoInkomenPartnerTweedePeriode:
      partnerInkomstenPeriodeTweede?.restInkomenBedrag,
    brutoInkomenPartnerDerdePeriode:
      partnerInkomstenPeriodeDerde?.restInkomenBedrag,
  };

  const jaarlijkseAnalyse = {
    gewenstInkomen: normGegevens?.gewenstBrutoInkomenBedrag ?? null,
    maandelijksTekortEerste: Math.max(
      0,
      ((normGegevens?.gewenstBrutoInkomenBedrag || 0) -
        (inkomensData.totaalInkomenEerstePeriode || 0)) /
        12,
    ),
    maandelijksTekortTweede: Math.max(
      0,
      ((normGegevens?.gewenstBrutoInkomenBedrag || 0) -
        (inkomensData.totaalInkomenTweedePeriode || 0)) /
        12,
    ),
    maandelijksTekortDerde: Math.max(
      0,
      ((normGegevens?.gewenstBrutoInkomenBedrag || 0) -
        (inkomensData.totaalInkomenDerdePeriode || 0)) /
        12,
    ),
  };

  if (loading || !motiveringOutput || !motiveringInkomensanalyse) {
    return <div className="spinner">Laden...</div>;
  }

  const firstResult = inkomensanalyseResultaten?.[0];
  const secondResult = inkomensanalyseResultaten?.[1];
  const thirdResult = inkomensanalyseResultaten?.[2];

  if (
    loading ||
    !motiveringOutput ||
    !motiveringInkomensanalyse ||
    !firstResult ||
    !normGegevens
  ) {
    return <div className="spinner">Laden...</div>;
  }

  const incomeColumns: Column[] = [
    {
      header: "",
      accessor: "period",
      align: "right",
    },
    {
      header: "",
      accessor: "verdienCapaciteit",
      className: "font-weight-bold",
      align: "right",
    },
    {
      header: `Restinkomen ${inkomensData.naamPersoon}`,
      accessor: "restinkomenAanvrager",
      dataType: "currency" as const,
      filterNullAnd0: false,
    },
    ...(inkomensData?.aoUitkeringAanvragerEerstePeriode !== 0 ||
    inkomensData?.aoUitkeringAanvragerTweedePeriode !== 0 ||
    inkomensData?.aoUitkeringAanvragerDerdePeriode !== 0
      ? [
          {
            header: "Arbeidsongeschiktheidverzekering",
            accessor: "aoUitkeringAanvrager",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.woonlastenEerstePeriode !== 0 ||
    inkomensData?.woonlastenTweedePeriode !== 0 ||
    inkomensData?.woonlastenDerdePeriode !== 0
      ? [
          {
            header: (
              <>
                Woonlastenverzekering{" "}
                <CitationRef type={ToelichtingOptions.WoonlastenVerzekering} />
              </>
            ),
            accessor: "woonlastenverzekering",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.wgaGatVerzekeringEerstePeriode !== 0 ||
    inkomensData?.wgaGatVerzekeringTweedePeriode !== 0 ||
    inkomensData?.wgaGatVerzekeringDerdePeriode !== 0
      ? [
          {
            header: (
              <>
                WGA-gat verzekering{" "}
                <CitationRef type={ToelichtingOptions.Brutering} />
              </>
            ),
            accessor: "wgaGatVerzekering",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.wiaExcedentVerzekeringEerstePeriode !== 0 ||
    inkomensData?.wiaExcedentVerzekeringTweedePeriode !== 0 ||
    inkomensData?.wiaExcedentVerzekeringDerdePeriode !== 0
      ? [
          {
            header: (
              <>
                WIA-excedent verzekering{" "}
                <CitationRef type={ToelichtingOptions.Brutering} />
              </>
            ),
            accessor: "wiaExcedentVerzekering",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.ivaUitkeringAanvragerEerstePeriode !== 0 ||
    inkomensData?.ivaUitkeringAanvragerTweedePeriode !== 0 ||
    inkomensData?.ivaUitkeringAanvragerDerdePeriode !== 0
      ? [
          {
            header: `Uitkering IVA ${inkomensData.naamPersoon}`,
            accessor: "ivaUitkering",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.wgaUitkeringAanvragerEerstePeriode !== 0 ||
    inkomensData?.wgaUitkeringAanvragerTweedePeriode !== 0 ||
    inkomensData?.wgaUitkeringAanvragerDerdePeriode !== 0
      ? [
          {
            header: `Uitkering WGA ${inkomensData.naamPersoon}`,
            accessor: "wgaUitkering",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.werkgeverspensioenEerstePeriode !== 0 ||
    inkomensData?.werkgeverspensioenTweedePeriode !== 0 ||
    inkomensData?.werkgeverspensioenDerdePeriode !== 0
      ? [
          {
            header: `Werkgeverspensioen ${inkomensData.naamPersoon}`,
            accessor: "werkgeverspensioen",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.invaliditeitspensioenEerstePeriode !== 0 ||
    inkomensData?.invaliditeitspensioenTweedePeriode !== 0 ||
    inkomensData?.invaliditeitspensioenDerdePeriode !== 0
      ? [
          {
            header: "Invaliditeitspensioen",
            accessor: "invaliditeitspensioen",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.beleggingInkomenEerste !== 0 ||
    inkomensData?.beleggingInkomenTweede !== 0 ||
    inkomensData?.beleggingInkomenDerde !== 0
      ? [
          {
            header: (
              <>
                Inkomsten uit belegging(en) {inkomensData.naamPersoon}{" "}
                <CitationRef type={ToelichtingOptions.Brutering} />
              </>
            ),
            accessor: "beleggingInkomen",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.verhuurInkomenEerste !== 0 ||
    inkomensData?.verhuurInkomenTweede !== 0 ||
    inkomensData?.verhuurInkomenDerde !== 0
      ? [
          {
            header: (
              <>
                Inkomsten uit verhuur {inkomensData.naamPersoon}{" "}
                <CitationRef type={ToelichtingOptions.Brutering} />
              </>
            ),
            accessor: "verhuurInkomen",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(motiveringInkomensanalyse?.aanvrager2
      ? [
          {
            header: `Inkomen ${inkomensData.naamPartner}`,
            accessor: "inkomenPartner",
            dataType: "currency" as const,
          },
          ...(inkomensData?.beleggingInkomenPartnerEerste !== 0 ||
          inkomensData?.beleggingInkomenPartnerTweede !== 0 ||
          inkomensData?.beleggingInkomenPartnerDerde !== 0
            ? [
                {
                  header: (
                    <>
                      Inkomsten uit belegging(en) {inkomensData.naamPartner}{" "}
                      <CitationRef type={ToelichtingOptions.Brutering} />
                    </>
                  ),
                  accessor: "beleggingInkomenPartner",
                  dataType: "currency" as const,
                },
              ]
            : []),
          ...(inkomensData?.verhuurInkomenPartnerEerste !== 0 ||
          inkomensData?.verhuurInkomenPartnerTweede !== 0 ||
          inkomensData?.verhuurInkomenPartnerDerde !== 0
            ? [
                {
                  header: (
                    <>
                      Inkomsten uit verhuur {inkomensData.naamPartner}{" "}
                      <CitationRef type={ToelichtingOptions.Brutering} />
                    </>
                  ),
                  accessor: "verhuurInkomenPartner",
                  dataType: "currency" as const,
                },
              ]
            : []),
        ]
      : []),
    ...(inkomensData?.inkomstenOverigEerstePeriode !== 0 ||
    inkomensData?.inkomstenOverigTweedePeriode !== 0 ||
    inkomensData?.inkomstenOverigDerdePeriode !== 0
      ? [
          {
            header: (
              <>
                Inkomsten overig {inkomensData.naamPersoon}{" "}
                <CitationRef type={ToelichtingOptions.Brutering} />
              </>
            ),
            accessor: "inkomstenOverig",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.inkomstenOverigPartnerEerstePeriode !== 0 ||
    inkomensData?.inkomstenOverigPartnerTweedePeriode !== 0 ||
    inkomensData?.inkomstenOverigPartnerDerdePeriode !== 0
      ? [
          {
            header: (
              <>
                Inkomsten overig {inkomensData.naamPartner}{" "}
                <CitationRef type={ToelichtingOptions.Brutering} />
              </>
            ),
            accessor: "inkomstenOverigPartner",
            dataType: "currency" as const,
          },
        ]
      : []),
    {
      header: "Inkomen totaal",
      accessor: "totaalInkomen",
      dataType: "currency" as const,
      className: "text-right font-weight-bold",
    },
    {
      header: "Inkomensverlies",
      accessor: "inkomensverlies",
      dataType: "currency" as const,
      className: "text-right table-warning",
      violation: (value: number) => value > 0,
    },
  ];

  const emptyColumnInkomen = {
    period: "",
    verdienCapaciteit: <></>,
    aoUitkeringAanvrager: null,
    restinkomenAanvrager: null,
    woonlastenverzekering: null,
    wgaGatVerzekering: null,
    wiaExcedentVerzekering: null,
    ivaUitkering: null,
    wgaUitkering: null,
    werkgeverspensioen: null,
    invaliditeitspensioen: null,
    beleggingInkomen: null,
    verhuurInkomen: null,
    inkomstenOverig: null,
    inkomstenOverigPartner: null,
    inkomenPartner: null,
    beleggingInkomenPartner: null,
    verhuurInkomenPartner: null,
    totaalInkomen: null,
    inkomensverlies: null,
  };

  const emptyColumnControleMaandlast = {
    benodigdInkomenBedrag: null,
    tekortBenodigInkomenBedrag: null,
    tekortNettoInkomenBedrag: null,
  };

  const incomeData = [
    {
      period: `${inkomensData?.percentageAoEerste}% AO`,
      verdienCapaciteit: (
        <>
          {inkomensData?.percentageVerdienEerste}% WERKEN{" "}
          <CitationRef type={ToelichtingOptions.Verdiencapaciteit1} />
        </>
      ),
      aoUitkeringAanvrager: getMonthlyAmount(
        inkomensData?.aoUitkeringAanvragerEerstePeriode,
      ),
      restinkomenAanvrager: getMonthlyAmount(
        inkomensData?.inkomenAanvragerEerstePeriode,
      ),
      woonlastenverzekering: getMonthlyAmount(
        inkomensData?.woonlastenEerstePeriode,
      ),
      wgaGatVerzekering: getMonthlyAmount(
        inkomensData?.wgaGatVerzekeringEerstePeriode,
      ),
      wiaExcedentVerzekering: getMonthlyAmount(
        inkomensData?.wiaExcedentVerzekeringEerstePeriode,
      ),
      ivaUitkering: getMonthlyAmount(
        inkomensData?.ivaUitkeringAanvragerEerstePeriode,
      ),
      wgaUitkering: getMonthlyAmount(
        inkomensData?.wgaUitkeringAanvragerEerstePeriode,
      ),
      werkgeverspensioen: getMonthlyAmount(
        inkomensData?.werkgeverspensioenEerstePeriode,
      ),
      invaliditeitspensioen: getMonthlyAmount(
        inkomensData?.invaliditeitspensioenEerstePeriode,
      ),
      beleggingInkomen: getMonthlyAmount(inkomensData?.beleggingInkomenEerste),
      verhuurInkomen: getMonthlyAmount(inkomensData?.verhuurInkomenEerste),
      inkomstenOverig: getMonthlyAmount(
        inkomensData?.inkomstenOverigEerstePeriode,
      ),
      inkomstenOverigPartner: getMonthlyAmount(
        inkomensData?.inkomstenOverigPartnerEerstePeriode,
      ),
      inkomenPartner: getMonthlyAmount(
        inkomensData?.inkomenPartnerEerstePeriode,
      ),
      beleggingInkomenPartner: getMonthlyAmount(
        inkomensData?.beleggingInkomenPartnerEerste,
      ),
      verhuurInkomenPartner: getMonthlyAmount(
        inkomensData?.verhuurInkomenPartnerEerste,
      ),
      totaalInkomen: getMonthlyAmount(inkomensData?.totaalInkomenEerstePeriode),
      inkomensverlies: Math.abs(inkomensData?.inkomensverliesEerste || 0),
    },
    ...(inkomensData?.percentageAoTweede > 0
      ? [
          {
            period: `${inkomensData?.percentageAoTweede}% AO`,
            verdienCapaciteit: (
              <>
                {inkomensData?.percentageVerdienTweede}% WERKEN{" "}
                <CitationRef type={ToelichtingOptions.Verdiencapaciteit2} />
              </>
            ),
            aoUitkeringAanvrager: getMonthlyAmount(
              inkomensData?.aoUitkeringAanvragerTweedePeriode,
            ),
            restinkomenAanvrager: getMonthlyAmount(
              inkomensData?.inkomenAanvragerTweedePeriode,
            ),
            woonlastenverzekering: getMonthlyAmount(
              inkomensData?.woonlastenTweedePeriode,
            ),
            wgaGatVerzekering: getMonthlyAmount(
              inkomensData?.wgaGatVerzekeringTweedePeriode,
            ),
            wiaExcedentVerzekering: getMonthlyAmount(
              inkomensData?.wiaExcedentVerzekeringTweedePeriode,
            ),
            ivaUitkering: getMonthlyAmount(
              inkomensData?.ivaUitkeringAanvragerTweedePeriode,
            ),
            wgaUitkering: getMonthlyAmount(
              inkomensData?.wgaUitkeringAanvragerTweedePeriode,
            ),
            werkgeverspensioen: getMonthlyAmount(
              inkomensData?.werkgeverspensioenTweedePeriode,
            ),
            invaliditeitspensioen: getMonthlyAmount(
              inkomensData?.invaliditeitspensioenTweedePeriode,
            ),
            beleggingInkomen: getMonthlyAmount(
              inkomensData?.beleggingInkomenTweede,
            ),
            verhuurInkomen: getMonthlyAmount(
              inkomensData?.verhuurInkomenTweede,
            ),
            inkomstenOverig: getMonthlyAmount(
              inkomensData?.inkomstenOverigTweedePeriode,
            ),
            inkomstenOverigPartner: getMonthlyAmount(
              inkomensData?.inkomstenOverigPartnerTweedePeriode,
            ),
            inkomenPartner: getMonthlyAmount(
              inkomensData?.inkomenPartnerTweedePeriode,
            ),
            beleggingInkomenPartner: getMonthlyAmount(
              inkomensData?.beleggingInkomenPartnerTweede,
            ),
            verhuurInkomenPartner: getMonthlyAmount(
              inkomensData?.verhuurInkomenPartnerTweede,
            ),
            totaalInkomen: getMonthlyAmount(
              inkomensData?.totaalInkomenTweedePeriode,
            ),
            inkomensverlies: Math.abs(inkomensData?.inkomensverliesTweede || 0),
          },
        ]
      : [emptyColumnInkomen]),
    ...(inkomensData?.percentageAoDerde > 0
      ? [
          {
            period: `${inkomensData?.percentageAoDerde}% AO`,
            verdienCapaciteit: (
              <>
                {inkomensData?.percentageVerdienDerde}% WERKEN{" "}
                <CitationRef type={ToelichtingOptions.Verdiencapaciteit3} />
              </>
            ),
            aoUitkeringAanvrager: getMonthlyAmount(
              inkomensData?.aoUitkeringAanvragerDerdePeriode,
            ),
            restinkomenAanvrager: getMonthlyAmount(
              inkomensData?.inkomenAanvragerDerdePeriode,
            ),
            woonlastenverzekering: getMonthlyAmount(
              inkomensData?.woonlastenDerdePeriode,
            ),
            wgaGatVerzekering: getMonthlyAmount(
              inkomensData?.wgaGatVerzekeringDerdePeriode,
            ),
            wiaExcedentVerzekering: getMonthlyAmount(
              inkomensData?.wiaExcedentVerzekeringDerdePeriode,
            ),
            ivaUitkering: getMonthlyAmount(
              inkomensData?.ivaUitkeringAanvragerDerdePeriode,
            ),
            wgaUitkering: getMonthlyAmount(
              inkomensData?.wgaUitkeringAanvragerDerdePeriode,
            ),
            werkgeverspensioen: getMonthlyAmount(
              inkomensData?.werkgeverspensioenDerdePeriode,
            ),
            invaliditeitspensioen: getMonthlyAmount(
              inkomensData?.invaliditeitspensioenDerdePeriode,
            ),
            beleggingInkomen: getMonthlyAmount(
              inkomensData?.beleggingInkomenDerde,
            ),
            verhuurInkomen: getMonthlyAmount(inkomensData?.verhuurInkomenDerde),
            inkomstenOverig: getMonthlyAmount(
              inkomensData?.inkomstenOverigDerdePeriode,
            ),
            inkomstenOverigPartner: getMonthlyAmount(
              inkomensData?.inkomstenOverigPartnerDerdePeriode,
            ),
            inkomenPartner: getMonthlyAmount(
              inkomensData?.inkomenPartnerDerdePeriode,
            ),
            beleggingInkomenPartner: getMonthlyAmount(
              inkomensData?.beleggingInkomenPartnerDerde,
            ),
            verhuurInkomenPartner: getMonthlyAmount(
              inkomensData?.verhuurInkomenPartnerDerde,
            ),
            totaalInkomen: getMonthlyAmount(
              inkomensData?.totaalInkomenDerdePeriode,
            ),
            inkomensverlies: Math.abs(inkomensData?.inkomensverliesDerde || 0),
          },
        ]
      : [emptyColumnInkomen]),
  ];

  const filteredIncomeData = incomeData;

  return (
    <CitationProvider
      voetnoten={voetnoten}
      excludedFootnotes={excludedFootnotes}
    >
      <div
        className={`container-fluid mt-2 px-2 ${classNames("", {
          spinner: loading,
        })}`}
      >
        {!loading && motiveringInkomensanalyse && inkomensData && (
          <Heading
            level={3}
            text={`Inkomen over ${(motiveringInkomensanalyse.toetsjaar ?? 0) -
              1} jaar bij arbeidsongeschiktheid van ${
              selectedAanvrager === "Aanvrager1"
                ? motiveringOutput.klantNaamAanvrager
                : motiveringOutput.klantNaamPartner
            }`}
            ignoreNumbers
            ignoreTOC
            className="mb-2"
          />
        )}
        <Table
          columns={incomeColumns}
          data={filteredIncomeData}
          id="inkomen-arbeidsongeschiktheid-scenario-table"
          rotate
          regularStyles
          className="scenario-ao-table"
          extraStyle=".scenario-ao-table th:first-child {
              width: 256px;
          }" /* eerste kolom extra breed maken */
        />
        {brutoInkomensWensenWeergeven && (
          <>
            <BrutoInkomensWensenTable
              brutoInkomensWensPercentage={brutoInkomensWensPercentage}
              jaarlijkseAnalyse={{
                maandelijksTekorten: [
                  jaarlijkseAnalyse.maandelijksTekortEerste,
                  jaarlijkseAnalyse.maandelijksTekortTweede,
                  jaarlijkseAnalyse.maandelijksTekortDerde,
                ],
              }}
              gewenstInkomenBedrag={jaarlijkseAnalyse.gewenstInkomen}
            />
          </>
        )}
        {controleWerkelijkeLastenWeergeven && (
          <ControleWerkelijkeLastenTable
            toegestaneLastBedragen={[
              firstResult.toegestaneLastBedrag ?? null,
              secondResult?.toegestaneLastBedrag ?? null,
              thirdResult?.toegestaneLastBedrag ?? null,
            ]}
            werkelijkeLastBedragen={[
              firstResult.werkelijkeLastBedrag ?? null,
              secondResult?.werkelijkeLastBedrag ?? null,
              thirdResult?.werkelijkeLastBedrag ?? null,
            ]}
            tekortToegestaneLastBedragen={[
              firstResult.tekortToegestaneLastBedrag ?? null,
              secondResult?.tekortToegestaneLastBedrag ?? null,
              thirdResult?.tekortToegestaneLastBedrag ?? null,
            ]}
          />
        )}

        {controleMaandlast &&
          controleMaandlastKeuze ===
            ControleMaandlastOptions.BenodigdInkomen && (
            <BenodigdInkomenTable
              data={[
                {
                  benodigdInkomenBedrag: firstResult.benodigdInkomenBedrag,
                  tekortBenodigInkomenBedrag:
                    firstResult.tekortBenodigInkomenBedrag,
                  tekortNettoInkomenBedrag:
                    firstResult.tekortNettoInkomenBedrag,
                },
                ...(secondResult
                  ? [
                      {
                        benodigdInkomenBedrag:
                          secondResult.benodigdInkomenBedrag,
                        tekortBenodigInkomenBedrag:
                          secondResult.tekortBenodigInkomenBedrag,
                        tekortNettoInkomenBedrag:
                          secondResult.tekortNettoInkomenBedrag,
                      },
                    ]
                  : [emptyColumnControleMaandlast]),
                ...(thirdResult
                  ? [
                      {
                        benodigdInkomenBedrag:
                          thirdResult.benodigdInkomenBedrag,
                        tekortBenodigInkomenBedrag:
                          thirdResult.tekortBenodigInkomenBedrag,
                        tekortNettoInkomenBedrag:
                          thirdResult.tekortNettoInkomenBedrag,
                      },
                    ]
                  : [emptyColumnControleMaandlast]),
              ]}
            />
          )}
        {maximaleHypotheekWeergeven && (
          <MaximaleHypotheekTable
            inkomensanalyseResultaten={inkomensanalyseResultaten}
            showAll={true}
          />
        )}
        {controleMaandlast && (
          <ControleMaandlastTable
            conclusies={[conclusieEerste, conclusieTweede, conclusieDerde]}
            controleMaandlastKeuze={controleMaandlastKeuze}
          />
        )}
        <div className="mt-3 px-2">
          {voetnoten?.map((footnote: ToelichtingRegel) => (
            <CitationNote
              key={footnote.type}
              type={footnote.type}
              text={footnote.toelichting || ""}
            />
          ))}
        </div>
        {nbiWeergeven && (
          <TableNettoBesteedbaarInkomen
            startDatum={motiveringInkomensanalyse?.startdatum}
            nbi={nbi.map((jaar) => ({
              ...jaar,
              bijzonderheden: jaar.bijzonderheden
                ? jaar.bijzonderheden.map((b: any) =>
                    b.bijzonderheid === null
                      ? null
                      : { bijzonderheid: b.bijzonderheid },
                  )
                : null,
            }))}
            voetnoot={nbiVoetnoot}
          />
        )}
        {benodigdKapitaalWeergeven && (
          <BenodigdKapitaalTable
            benodigdKapitaal={benodigdKapitaal}
            benodigdKapitaalOmschrijving={benodigdKapitaalOmschrijving}
          />
        )}
      </div>
    </CitationProvider>
  );
};
